import { FC, useEffect, useMemo, useRef, useState } from 'react';
import axiosInstance from '../../main/initializers/axios';
import { ApiConstants } from '../../main/constants/ApiConstants';
import ITable from '../../main/interfaces/ITable';
import RestaurantTable from './restaurantTable/RestaurantTable';
import GeneralModal from '../../main/components/modal/general/GeneralModal';
import BasicCli from '../../main/components/basicCli/BasicCli';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import './Restaurant.css';
import {
  RoomCustomizationDto,
  TableWithLayout,
  applyLayoutToTables,
  initTablePositions,
  setDefaultRoomInList,
} from './restaurantLayoutShared';

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const RestaurantConfiguration: FC = () => {
  const [tables, setTables] = useState<TableWithLayout[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rooms, setRooms] = useState<RoomCustomizationDto[]>([]);
  const [selectedRoomId, setSelectedRoomId] = useState<number | null>(null);
  const [selectedRoomIsDefault, setSelectedRoomIsDefault] = useState(false);
  const [roomModalOpen, setRoomModalOpen] = useState(false);
  const [newRoomCode, setNewRoomCode] = useState('');
  const [newRoomName, setNewRoomName] = useState('');
  const [newRoomIsDefault, setNewRoomIsDefault] = useState(false);

  const moveTimerRef = useRef<number | null>(null);
  const { i18n } = useTranslation();

  const loadBaseTables = async () => {
    const tableResp = await axiosInstance.post(ApiConstants.tables.list);
    return initTablePositions((tableResp.data || []) as ITable[]);
  };

  const loadInitialData = async () => {
    try {
      const [baseTables, roomResp] = await Promise.all([loadBaseTables(), axiosInstance.get('/api/roomcustomization/list')]);
      const roomList = (roomResp.data || []) as RoomCustomizationDto[];
      const initialRoom = roomList.find((room) => room.isDefault) ?? roomList[0] ?? null;

      if (initialRoom) {
        const layoutResp = await axiosInstance.get(`/api/roomcustomization/layout?roomCustomizationId=${initialRoom.id}`);
        setTables(applyLayoutToTables(baseTables, layoutResp.data || []));
        setSelectedRoomId(initialRoom.id);
        setSelectedRoomIsDefault(!!initialRoom.isDefault);
      } else {
        setTables(baseTables);
      }

      setRooms(roomList);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    void loadInitialData();

    return () => {
      stopMove();
    };
  }, []);

  const selectedTable = useMemo(() => tables.find((table) => table.id === selectedId) || null, [selectedId, tables]);
  const selectedRoom = useMemo(() => rooms.find((room) => room.id === selectedRoomId) || null, [rooms, selectedRoomId]);

  const handleSelect = (id: number) => {
    setSelectedId(id);
  };

  const moveOnce = (dx: number, dy: number) => {
    if (!selectedId) return;

    setTables((prev) =>
      prev.map((table) =>
        table.id === selectedId
          ? {
              ...table,
              posX: clamp(table.posX + dx, 0, 100),
              posY: clamp(table.posY + dy, 0, 100),
            }
          : table,
      ),
    );
  };

  const scheduleMove = (dx: number, dy: number, delay: number) => {
    moveTimerRef.current = window.setTimeout(() => {
      moveOnce(dx, dy);
      scheduleMove(dx, dy, Math.max(40, delay - 15));
    }, delay);
  };

  const startMove = (dx: number, dy: number) => {
    if (!selectedId) return;
    stopMove();
    scheduleMove(dx, dy, 160);
  };

  const stopMove = () => {
    if (moveTimerRef.current !== null) {
      window.clearTimeout(moveTimerRef.current);
      moveTimerRef.current = null;
    }
  };

  const changeShape = (tableType: number) => {
    if (!selectedId) return;
    setTables((prev) => prev.map((table) => (table.id === selectedId ? { ...table, tableType } : table)));
  };

  const handleSaveLayout = async () => {
    if (!selectedRoomId) {
      setRoomModalOpen(true);
      return;
    }

    const payload = {
      roomCustomizationId: selectedRoomId,
      isDefault: selectedRoomIsDefault,
      tables: tables.map((table) => ({
        tableId: table.id,
        posX: table.posX,
        posY: table.posY,
      })),
    };

    try {
      await axiosInstance.post('/api/roomcustomization/save-layout', payload);
      setRooms((prev) => setDefaultRoomInList(prev, selectedRoomId, selectedRoomIsDefault));
      toast.success(i18n.t('restaurant.layoutSaved', { defaultValue: 'Layout saved' }));
    } catch {
      toast.error(i18n.t('restaurant.layoutSaveError', { defaultValue: 'Error saving layout' }));
    }
  };

  const handleCreateRoomAndSave = async () => {
    if (!newRoomCode || !newRoomName) return;

    try {
      const roomResp = await axiosInstance.post('/api/roomcustomization/create', {
        code: newRoomCode,
        name: newRoomName,
        isDefault: newRoomIsDefault,
      });

      const newRoom = roomResp.data as RoomCustomizationDto;
      const roomWithDefault = { ...newRoom, isDefault: newRoomIsDefault || newRoom.isDefault };

      await axiosInstance.post('/api/roomcustomization/save-layout', {
        roomCustomizationId: roomWithDefault.id,
        isDefault: !!roomWithDefault.isDefault,
        tables: tables.map((table) => ({
          tableId: table.id,
          posX: table.posX,
          posY: table.posY,
        })),
      });

      setRooms((prev) => {
        const next = newRoomIsDefault ? prev.map((room) => ({ ...room, isDefault: false })) : [...prev];
        return [...next, roomWithDefault];
      });
      setSelectedRoomId(roomWithDefault.id);
      setSelectedRoomIsDefault(!!roomWithDefault.isDefault);
      setRoomModalOpen(false);
      setNewRoomCode('');
      setNewRoomName('');
      setNewRoomIsDefault(false);

      toast.success(i18n.t('restaurant.roomCreatedAndSaved', { defaultValue: 'Room created and layout saved' }));
    } catch {
      toast.error(i18n.t('restaurant.roomCreateSaveError', { defaultValue: 'Error creating room / saving layout' }));
    }
  };

  const handleSelectExistingRoom = async () => {
    if (!selectedRoomId) return;

    try {
      const [baseTables, layoutResp] = await Promise.all([
        loadBaseTables(),
        axiosInstance.get(`/api/roomcustomization/layout?roomCustomizationId=${selectedRoomId}`),
      ]);
      const room = rooms.find((item) => item.id === selectedRoomId) || null;

      setTables(applyLayoutToTables(baseTables, layoutResp.data || []));
      setSelectedRoomIsDefault(!!room?.isDefault);
      setRoomModalOpen(false);
    } catch {
      toast.error(i18n.t('restaurant.layoutLoadError', { defaultValue: 'Error loading layout' }));
    }
  };

  const handleRoomPickerChange = (roomIdValue: string) => {
    const roomId = roomIdValue ? Number(roomIdValue) : null;
    const room = rooms.find((item) => item.id === roomId) || null;

    setSelectedRoomId(roomId);
    setSelectedRoomIsDefault(!!room?.isDefault);
  };

  return (
    <BasicCli>
      <div className="restPage">
        <div className="bcCard restCard">
          <div className="rest-wrapper">
            <div className="rest-header">
              <div className="rest-header-info">
                <span className="rest-title">{i18n.t('restaurant.configurationTitle', { defaultValue: 'Room customization' })}</span>
                <span className="rest-subtitle">
                  {i18n.t('restaurant.room', { defaultValue: 'Room' })}: {selectedRoom ? selectedRoom.name : i18n.t('generic.none', { defaultValue: 'None' })}
                </span>
              </div>

              <div className="rest-header-actions">
                <div className="rest-group">
                  <span className="rest-group-label">{i18n.t('restaurant.move', { defaultValue: 'Move' })}</span>
                  <button
                    className="ui-control ui-btn rest-btn"
                    disabled={!selectedTable}
                    onMouseDown={() => startMove(-1, 0)}
                    onMouseUp={stopMove}
                    onMouseLeave={stopMove}
                    onTouchStart={() => startMove(-1, 0)}
                    onTouchEnd={stopMove}
                  >
                    {i18n.t('generic.left', { defaultValue: 'Left' })}
                  </button>
                  <button
                    className="ui-control ui-btn rest-btn"
                    disabled={!selectedTable}
                    onMouseDown={() => startMove(1, 0)}
                    onMouseUp={stopMove}
                    onMouseLeave={stopMove}
                    onTouchStart={() => startMove(1, 0)}
                    onTouchEnd={stopMove}
                  >
                    {i18n.t('generic.right', { defaultValue: 'Right' })}
                  </button>
                  <button
                    className="ui-control ui-btn rest-btn"
                    disabled={!selectedTable}
                    onMouseDown={() => startMove(0, -1)}
                    onMouseUp={stopMove}
                    onMouseLeave={stopMove}
                    onTouchStart={() => startMove(0, -1)}
                    onTouchEnd={stopMove}
                  >
                    {i18n.t('generic.up', { defaultValue: 'Up' })}
                  </button>
                  <button
                    className="ui-control ui-btn rest-btn"
                    disabled={!selectedTable}
                    onMouseDown={() => startMove(0, 1)}
                    onMouseUp={stopMove}
                    onMouseLeave={stopMove}
                    onTouchStart={() => startMove(0, 1)}
                    onTouchEnd={stopMove}
                  >
                    {i18n.t('generic.down', { defaultValue: 'Down' })}
                  </button>
                </div>

                <div className="rest-group">
                  <select
                    className="ui-control ui-select rest-select"
                    disabled={!selectedTable}
                    value={selectedTable?.tableType ?? 1}
                    onChange={(e) => changeShape(Number(e.target.value))}
                  >
                    <option value={1}>{i18n.t('restaurant.round4', { defaultValue: 'Round 4' })}</option>
                    <option value={2}>{i18n.t('restaurant.round6', { defaultValue: 'Round 6' })}</option>
                    <option value={3}>{i18n.t('restaurant.round8', { defaultValue: 'Round 8' })}</option>
                    <option value={4}>{i18n.t('restaurant.rect4', { defaultValue: 'Rect 4' })}</option>
                    <option value={5}>{i18n.t('restaurant.rect6', { defaultValue: 'Rect 6' })}</option>
                    <option value={6}>{i18n.t('restaurant.rect8', { defaultValue: 'Rect 8' })}</option>
                  </select>
                </div>

                <label className="rest-check">
                  <input
                    type="checkbox"
                    checked={selectedRoomIsDefault}
                    disabled={!selectedRoomId}
                    onChange={(e) => setSelectedRoomIsDefault(e.target.checked)}
                  />
                  <span>{i18n.t('restaurant.defaultRoom', { defaultValue: 'Default room' })}</span>
                </label>

                <button className="ui-control ui-btn rest-btn" type="button" onClick={() => setRoomModalOpen(true)}>
                  {i18n.t('restaurant.roomButton', { defaultValue: 'Room...' })}
                </button>
                <button className="ui-control ui-btn ui-btn--primary rest-btn" type="button" onClick={handleSaveLayout}>
                  {i18n.t('restaurant.saveLayout', { defaultValue: 'Save layout' })}
                </button>
              </div>
            </div>

            <div className="restBody">
              <div className="rest-canvas">
                <div className="rest-canvas-surface">
                  {tables.map((table) => (
                    <div
                      key={table.id}
                      className={`rest-table-wrapper ${selectedId === table.id ? 'rest-table-selected' : ''}`}
                      style={{ left: `${table.posX}%`, top: `${table.posY}%` }}
                    >
                      <RestaurantTable
                        tableId={table.id!}
                        table={table}
                        status={table.isBusy ? 'booked' : 'available'}
                        timer={table.isBusy ? '5m 30s' : ''}
                        tableCode={table.code}
                        selected={selectedId === table.id}
                        onSelect={handleSelect}
                        isRounded={false}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {roomModalOpen && (
        <GeneralModal
          headerName={i18n.t('restaurant.configurationTitle', { defaultValue: 'Room customization' })}
          hideFooter
          width="500px"
          minwidth="400px"
          isFullScreen={false}
          handleClose={() => setRoomModalOpen(false)}
          handleClick={() => {}}
          headerColor="blue"
        >
            <div className="rest-room-modal">
            <div className="rest-room-block">
              <h4>{i18n.t('restaurant.selectExisting', { defaultValue: 'Select existing' })}</h4>
              <select className="ui-control ui-select rest-select full" value={selectedRoomId ?? ''} onChange={(e) => handleRoomPickerChange(e.target.value)}>
                <option value="">{i18n.t('restaurant.noneOption', { defaultValue: '-- none --' })}</option>
                {rooms.map((room) => (
                  <option key={room.id} value={room.id}>
                    {room.code} - {room.name} {room.isDefault ? `(${i18n.t('restaurant.defaultTag', { defaultValue: 'default' })})` : ''}
                  </option>
                ))}
              </select>
              <label className="rest-check">
                <input
                  type="checkbox"
                  checked={selectedRoomIsDefault}
                  disabled={!selectedRoomId}
                  onChange={(e) => setSelectedRoomIsDefault(e.target.checked)}
                />
                <span>{i18n.t('restaurant.defaultRoom', { defaultValue: 'Default room' })}</span>
              </label>
              <button className="ui-control ui-btn ui-btn--primary rest-btn mt-8" type="button" disabled={!selectedRoomId} onClick={handleSelectExistingRoom}>
                {i18n.t('restaurant.loadLayout', { defaultValue: 'Load layout' })}
              </button>
            </div>

            <div className="rest-room-block">
              <h4>{i18n.t('generic.createNew', { defaultValue: 'Create new' })}</h4>
              <input className="ui-control ui-input rest-input" placeholder={i18n.t('generic.code', { defaultValue: 'Code' })} value={newRoomCode} onChange={(e) => setNewRoomCode(e.target.value)} />
              <input className="ui-control ui-input rest-input" placeholder={i18n.t('generic.name', { defaultValue: 'Name' })} value={newRoomName} onChange={(e) => setNewRoomName(e.target.value)} />
              <label className="rest-check">
                <input type="checkbox" checked={newRoomIsDefault} onChange={(e) => setNewRoomIsDefault(e.target.checked)} />
                <span>{i18n.t('restaurant.defaultRoom', { defaultValue: 'Default room' })}</span>
              </label>
              <button className="ui-control ui-btn ui-btn--primary rest-btn mt-8" type="button" onClick={handleCreateRoomAndSave}>
                {i18n.t('restaurant.createAndSaveLayout', { defaultValue: 'Create & save layout' })}
              </button>
            </div>
          </div>
        </GeneralModal>
      )}
    </BasicCli>
  );
};

export default RestaurantConfiguration;
